import {
  dedupeLiveEventsByEventId,
  getLiveEventDedupeKey,
  getLiveEventEventIdKey,
  LIVE_FEED_MAX_STORED_EVENTS,
  mergeUniqueByKey,
  sortLiveEventsAsc,
} from './liveFeedStreamUtils';

/**
 * Middleware: adapta la respuesta de `GET …/matches/{matchId}/live-match-actions`
 * al formato `liveEvents` del detalle de partido (p. ej. `matchDetailsMock.json`).
 *
 * @typedef {object} LiveEventPlayer
 * @property {string} playerId
 * @property {string} name
 * @property {number} number
 *
 * @typedef {object} LiveEventItem
 * @property {string} eventId
 * @property {string|null} tags
 * @property {string} type
 * @property {string} name
 * @property {string} [teamId]
 * @property {number} minute
 * @property {number} second
 * @property {string} minuteDisplay
 * @property {LiveEventPlayer} [player]
 * @property {LiveEventPlayer} [relatedPlayer]
 */

const ACTION_TYPE_TO_LIVE_EVENT = {
  SHOT_WIDE: 'SHOT_OFF_TARGET',
  SHOT_ON_GOAL: 'SHOT_ON_TARGET',
  FOUL_COMMITTED: 'FOUL',
  SAVE_BY_GOALKEEPER: 'SAVE',
};

/** Eventos compañeros del feed en stream que no existen en `liveEvents` del mock. */
const SKIPPED_STREAM_ACTION_TYPES = new Set(['SHOT_BLOCKED_BY']);
const NON_DISPLAY_STREAM_MAPPED_TYPES = new Set(['PERIOD_START', 'PERIOD_END']);

function hasAnyDisplayableEvent(events) {
  if (!Array.isArray(events) || events.length === 0) return false;
  return events.some((e) => e?.type && !NON_DISPLAY_STREAM_MAPPED_TYPES.has(e.type));
}

function isFirstHalfEndEvent(e) {
  if (!e || (e.type !== 'PERIOD_END' && e.type !== 'END_OF_PERIOD')) return false;
  const name = e.name != null ? String(e.name) : '';
  if (/primer|primera|1\.ª|medio|final de tiempo/i.test(name)) return true;
  const m = e.minute != null && e.minute !== '' ? Number(e.minute) : null;
  return Number.isFinite(m) ? m > 0 && m <= 47 : false;
}

function isMatchEndEvent(e) {
  if (!e || (e.type !== 'PERIOD_END' && e.type !== 'END_OF_PERIOD')) return false;
  const name = e.name != null ? String(e.name) : '';
  if (/fin del partido|final del partido|full time|match end|partido finalizado/i.test(name)) {
    return true;
  }
  const m = e.minute != null && e.minute !== '' ? Number(e.minute) : null;
  return Number.isFinite(m) ? m >= 90 : false;
}

function capEventsKeepingPinned(events, maxEvents) {
  if (!Array.isArray(events) || events.length <= maxEvents) return events;

  const keys = events.map((e, i) => getLiveEventDedupeKey(e) ?? `idx:${i}`);
  const pinnedKeySet = new Set();
  keys.forEach((k, i) => {
    const e = events[i];
    if (isFirstHalfEndEvent(e) || isMatchEndEvent(e)) {
      pinnedKeySet.add(k);
    }
  });

  const keepCount = Math.max(0, maxEvents - pinnedKeySet.size);
  const keepKeySet = new Set(pinnedKeySet);
  let kept = 0;
  for (let i = events.length - 1; i >= 0 && kept < keepCount; i -= 1) {
    const k = keys[i];
    if (keepKeySet.has(k)) continue;
    keepKeySet.add(k);
    kept += 1;
  }

  return events.filter((_, i) => keepKeySet.has(keys[i]));
}

/**
 * Dedupe por `eventId`, orden cronológico ascendente y tope (con anclas de periodo).
 * @param {object[]} events
 * @param {number} maxEvents
 */
function finalizeMergedLiveEvents(events, maxEvents) {
  const deduped = dedupeLiveEventsByEventId(events ?? []);
  const sorted = sortLiveEventsAsc(deduped);
  if (maxEvents == null || !Number.isFinite(maxEvents) || maxEvents <= 0) {
    return sorted;
  }
  return capEventsKeepingPinned(sorted, maxEvents);
}

/**
 * @param {string} mappedType
 * @param {number} periodNumber
 * @param {string} displayName
 * @param {object} [options]
 * @param {string} [options.periodType]
 * @param {number} [options.minute]
 */
function resolveLiveEventName(mappedType, periodNumber, displayName, options = {}) {
  const label = displayName != null ? String(displayName).trim() : '';
  const periodType =
    options.periodType != null ? String(options.periodType).trim() : '';
  const minute =
    options.minute != null && options.minute !== '' ? Number(options.minute) : null;

  if (mappedType === 'PERIOD_START') {
    return label;
  }

  if (mappedType === 'PERIOD_END') {
    return label;
  }

  if (mappedType === 'SUBSTITUTION_OUT' && (!label || /^cambio$/i.test(label))) {
    return 'Sale';
  }

  return label;
}

/**
 * @param {string} type
 * @param {number} periodNumber
 */
function mapStreamActionType(type, periodNumber) {
  if (!type) return '';
  if (type === 'KICK_OFF') return 'PERIOD_START';
  if (type === 'END_OF_PERIOD') return 'PERIOD_END';
  return ACTION_TYPE_TO_LIVE_EVENT[type] ?? type;
}

/**
 * @param {object} payload
 * @returns {LiveEventPlayer|null}
 */
function mapStreamParticipant(payload, prefix = 'participant') {
  const id = payload?.[`${prefix}Id`];
  const name = payload?.[`${prefix}Name`];
  const number = payload?.[`${prefix}Number`];

  if (id == null && (name == null || String(name).trim() === '')) {
    return null;
  }

  const player = {};
  if (id != null) player.playerId = String(id);
  if (name != null) player.name = String(name);
  if (number != null && number !== '') player.number = Number(number);
  return player;
}

/**
 * @param {object} streamEvent — ítem de `events` del stream (`action.recorded`).
 * @returns {LiveEventItem|null}
 */
export function mapStreamActionToLiveEvent(streamEvent) {
  if (streamEvent?.eventType !== 'action.recorded') return null;

  const payload = streamEvent.payload;
  if (!payload || typeof payload !== 'object') return null;

  const rawType = payload.type != null ? String(payload.type).trim() : '';
  if (!rawType || SKIPPED_STREAM_ACTION_TYPES.has(rawType)) return null;

  const periodNumber =
    payload.periodNumber != null ? Number(payload.periodNumber) : 1;
  const mappedType = mapStreamActionType(rawType, periodNumber);
  if (!mappedType) return null;

  const displayName =
    payload.displayName != null ? String(payload.displayName).trim() : '';

  const event = {
    eventId:
      payload.actionId != null
        ? String(payload.actionId)
        : streamEvent.eventId != null
          ? String(streamEvent.eventId)
          : '',
    tags:
      payload.tags != null && String(payload.tags).trim() !== ''
        ? String(payload.tags).trim()
        : null,
    type: mappedType,
    name: resolveLiveEventName(mappedType, periodNumber, displayName, {
      periodType: payload.periodType,
      minute: payload.minute,
    }),
    minute: payload.minute != null ? Number(payload.minute) : 0,
    second: payload.second != null ? Number(payload.second) : 0,
    minuteDisplay:
      payload.minuteDisplay != null ? String(payload.minuteDisplay) : '',
  };

  if (displayName) {
    event.displayName = displayName;
  }

  if (payload.teamId != null && String(payload.teamId).trim() !== '') {
    event.teamId = String(payload.teamId);
  }

  const participantType =
    payload.participantType != null ? String(payload.participantType).trim() : '';
  if (participantType === 'PERSON' || payload.participantId != null) {
    const player = mapStreamParticipant(payload, 'participant');
    if (player) event.player = player;
  }

  const relatedPlayer = mapStreamParticipant(payload, 'relatedPlayer');
  if (relatedPlayer) event.relatedPlayer = relatedPlayer;

  if (payload.homeScore != null && payload.awayScore != null) {
    event.homeScore = payload.homeScore;
    event.awayScore = payload.awayScore;
  }

  return event;
}

/**
 * @param {object|null|undefined} response — cuerpo axios o mock (`live-match-actions-mock.json`).
 * @returns {Array<{ matchId: string, events: LiveEventItem[] }>|null}
 */
export function mapLiveMatchActionsToLiveEvents(response) {
  if (response == null) return null;

  const existing =
    response?.data?.liveEvents ??
    response?.liveEvents ??
    (Array.isArray(response) ? response : null);

  if (Array.isArray(existing)) {
    return existing;
  }

  const source = response?.data ?? response;
  const matchId = source?.matchId != null ? String(source.matchId) : null;
  const streamEvents = source?.events;

  if (!matchId || !Array.isArray(streamEvents)) return null;

  const events = finalizeMergedLiveEvents(
    streamEvents.map(mapStreamActionToLiveEvent).filter(Boolean),
    Number.POSITIVE_INFINITY,
  );

  return [{ matchId, events }];
}

/**
 * Fusiona el bloque `liveEvents` previo con una respuesta de polling (sin duplicar por `eventId`).
 * @param {Array<{ matchId: string, events: LiveEventItem[] }>|null|undefined} previousLiveEvents
 * @param {object|null|undefined} response
 * @param {{ maxEvents?: number }} [options]
 * @returns {Array<{ matchId: string, events: LiveEventItem[] }>|null}
 */
function mergeLiveEventsBlocks(previousLiveEvents, incomingBlocks, maxEvents) {
  const incomingBlock = incomingBlocks?.[0];
  if (!incomingBlock) {
    return Array.isArray(previousLiveEvents) ? previousLiveEvents : incomingBlocks;
  }

  const prevBlock = Array.isArray(previousLiveEvents) ? previousLiveEvents[0] : null;

  if (!prevBlock?.events?.length) {
    const events = finalizeMergedLiveEvents(incomingBlock.events ?? [], maxEvents);
    return [{ ...incomingBlock, events }, ...(incomingBlocks?.slice(1) ?? [])];
  }

  if (
    prevBlock.matchId != null &&
    incomingBlock.matchId != null &&
    String(prevBlock.matchId) !== String(incomingBlock.matchId)
  ) {
    return incomingBlocks;
  }

  const mergedEvents = mergeUniqueByKey(
    prevBlock.events ?? [],
    incomingBlock.events ?? [],
    getLiveEventEventIdKey,
    Number.POSITIVE_INFINITY,
  );
  const events = finalizeMergedLiveEvents(mergedEvents, maxEvents);

  return [
    {
      ...incomingBlock,
      matchId: incomingBlock.matchId ?? prevBlock.matchId,
      events,
    },
    ...(incomingBlocks?.slice(1) ?? []),
  ];
}

/**
 * Fusiona el bloque `liveEvents` previo con una respuesta de polling (sin duplicar por `eventId`).
 * @param {Array<{ matchId: string, events: LiveEventItem[] }>|null|undefined} previousLiveEvents
 * @param {object|null|undefined} response
 * @param {{ maxEvents?: number }} [options]
 * @returns {Array<{ matchId: string, events: LiveEventItem[] }>|null}
 */
export function mergeLiveMatchActionsIntoLiveEvents(
  previousLiveEvents,
  response,
  options = {},
) {
  if (response == null) return null;

  const maxEvents = options.maxEvents ?? LIVE_FEED_MAX_STORED_EVENTS ?? null;
  const preformatted =
    response?.data?.liveEvents ??
    response?.liveEvents ??
    (Array.isArray(response) ? response : null);

  if (Array.isArray(preformatted)) {
    return mergeLiveEventsBlocks(previousLiveEvents, preformatted, maxEvents);
  }

  const mapped = mapLiveMatchActionsToLiveEvents(response);
  if (mapped == null) return null;

  // Caso real observado: el primer poll al entrar a Resumen a veces trae solo eventos de borde
  // (KICK_OFF/END_OF_PERIOD → PERIOD_START/PERIOD_END) o acciones omitidas.
  // Si ya tenemos historial previo, evitar sobrescribirlo por un batch "no mostrable" que deja la UI vacía.
  const prevBlock = Array.isArray(previousLiveEvents) ? previousLiveEvents[0] : null;
  const incomingBlock = mapped?.[0];
  if (
    hasAnyDisplayableEvent(prevBlock?.events) &&
    incomingBlock?.matchId != null &&
    prevBlock?.matchId != null &&
    String(incomingBlock.matchId) === String(prevBlock.matchId) &&
    !hasAnyDisplayableEvent(incomingBlock.events)
  ) {
    return previousLiveEvents;
  }

  return mergeLiveEventsBlocks(previousLiveEvents, mapped, maxEvents);
}
